import { ReactNode, useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { useFlags } from 'launchdarkly-react-client-sdk';

import PageSpinner from '@/components/PageSpinner';
import { useEmployeesMap } from '@/hooks/useEmployeesMap';
import { useAppSelector } from '@/redux/store';

type EmployeesMapGateProps = {
  children: ReactNode;
  /** Rendered when the first load of the employees map fails. */
  errorFallback: ReactNode;
};

const DIVISIONS_FLAG = 'ithDivisions';
const MAP_READY_TIMING = 'employees_map_ready';

const GateContent = ({ children, errorFallback }: EmployeesMapGateProps) => {
  const { isEnabled, isLoading, hasData, error } = useEmployeesMap();
  const { ithDivisions } = useFlags();

  const failedFirstLoad = isEnabled && !isLoading && !hasData && !!error;

  const isReady = isEnabled && !isLoading && hasData;

  // Lets RUM views be split by flag state, so performance can be compared
  // between organizations (or periods) with the map on and off. Records the
  // flag itself, not isEnabled: users without employees:read have no map even
  // when the flag is on, and must not count as "off".
  const isFlagOn = Boolean(ithDivisions);
  useEffect(() => {
    datadogRum.addFeatureFlagEvaluation(DIVISIONS_FLAG, isFlagOn);
  }, [isFlagOn]);

  // Time from the start of the view until page content is allowed to render,
  // i.e. how much of the load is spent waiting on the map.
  useEffect(() => {
    if (isReady) {
      datadogRum.addTiming(MAP_READY_TIMING);
    }
  }, [isReady]);

  useEffect(() => {
    if (failedFirstLoad) {
      window.DD_RUM?.addError('Employees map failed to load', {
        location: 'EmployeesMapGate',
        status: error && 'status' in error ? error.status : 'unknown',
      });
    }
  }, [failedFirstLoad, error]);

  if (!isEnabled) {
    return <>{children}</>;
  }

  // First load only: background refetches don't set isLoading, so pages
  // don't flash back to a spinner after an employee mutation.
  if (isLoading) {
    return <PageSpinner />;
  }

  // A failed background refetch keeps the last good data, so only a failed
  // first load blocks the page.
  if (failedFirstLoad) {
    return <>{errorFallback}</>;
  }

  return <>{children}</>;
};

/**
 * Holds page content until the centralized employees map has loaded, so no
 * page renders against an empty map and wrongly treats employees as hidden.
 * Navigation is rendered outside the gate, so it stays usable meanwhile.
 *
 * Renders children untouched when the map is disabled (flag off, or the user
 * can't list employees), or when there is no organization yet (for example
 * the org switcher), since the map can't be requested without an organization
 * id.
 */
export const EmployeesMapGate = (props: EmployeesMapGateProps) => {
  const hasOrganization = useAppSelector(state =>
    Boolean(state.loggedUser?.organizationId)
  );

  return hasOrganization ? <GateContent {...props} /> : <>{props.children}</>;
};
